// ============================================================================
// PAGE SECTIONS - Milbi Centre Microsite
// ============================================================================

import { ARButton } from "./ARButton";
import emitter from "@webcore/core/events/eventemitter";
import { getModelHash, ModelData } from "../data/data";
import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";

// Import assets
import MilbiLogo from "../assets/milbi-logo.webp";
import HeroBackground from "../assets/hero-background.webp";
import SeeInsideBg from "../assets/microsite/see-inside-bg.webp";
import ButchullaPeopleBg from "../assets/microsite/butchulla-people-bg.webp";
import ButchullaLoresBg from "../assets/microsite/butchulla-lores-bg.webp";
import ButchullaLoresGraphic from "../assets/microsite/butchulla-lores.webp";
import ButchullaLoresPoster from "../assets/microsite/butchulla-lores-poster.webp";
import ButchullaLoresVideo from "../assets/microsite/butchulla-lores.mp4";
import ButchullaCareBg from "../assets/microsite/butchulla-care-bg.webp";
import ButchullaCareVideo from "../assets/microsite/butchulla-care.mp4";
import ButchullaCareForCountryVideo from "../assets/microsite/butchulla-care-for-country.mp4";
import DaymanPointImage from "../assets/microsite/dayman-point.webp";
import SavingTurtles1 from "../assets/microsite/saving-turtles-1.webp";
import SavingTurtles21 from "../assets/microsite/saving-turtles-2-1.webp";
import SavingTurtles22 from "../assets/microsite/saving-turtles-2-2.webp";
import SavingTurtles3 from "../assets/microsite/saving-turtles-3.webp";
import SavingTurtles4 from "../assets/microsite/saving-turtles-4.webp";
import SavingTurtles5 from "../assets/microsite/saving-turtles-5.webp";
import SavingTurtles6 from "../assets/microsite/saving-turtles-6.webp";
import SavingTurtles7 from "../assets/microsite/saving-turtles-7.webp";
import SavingTurtles8 from "../assets/microsite/saving-turtles-8.jpg";
import MeetRescue1 from "../assets/microsite/meet-rescue-1.webp";
import MeetRescue2 from "../assets/microsite/meet-rescue-2.webp";
import MeetRescue3 from "../assets/microsite/meet-rescue-3.webp";
import ReportSighting from "../assets/microsite/report-sighting.webp";
import WatchOutHatchlings from "../assets/microsite/watch-out-hatchlings.webp";
import WatchOutHatchlings2Poster from "../assets/microsite/watch-out-hatchlings-2.webp";
import WatchOutHatchlingsVideo from "../assets/microsite/watch-out-hatchlings.mp4";
import TouristsContinue from "../assets/microsite/tourists-continue.webp";
import FollowReleasedTurtles from "../assets/microsite/follow-released-turtles.webp";
import KnowingThreatsVideo from "../assets/microsite/knowing-threats.mp4";
import KnowingThreatsPoster from "../assets/microsite/knowing-threats-poster.webp";
import RehabTurtlesVideo from "../assets/microsite/rehab-turtles.mp4";
import RehabTurtlesPoster from "../assets/microsite/rehab-turtles-poster.webp";
import WatchSeaTurtleCareVideo from "../assets/microsite/watch-sea-turtle-care.mp4";
import WatchSeaTurtleCarePoster from "../assets/microsite/watch-sea-turtle-care-poster.webp";
import UnderstandingBiologyVideo from "../assets/microsite/understanding-biology.mp4";
import UnderstandingBiologyPoster from "../assets/microsite/understanding-biology-poster.webp";
import DiscoveringTurtlesLiveImage from "../assets/microsite/discovering-turtles-live.webp";
import UntanglingCauseImage from "../assets/microsite/untangling-cause.webp";
import ActingLocallyImage from "../assets/microsite/acting-locally.webp";
import LogoWhite from "../assets/microsite/logo-white.webp";

// Header Component
type NavItem = {
  label: string;
  id: string;
};

type NavGroup = {
  heading: string;
  headingId: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    heading: "About",
    headingId: "reveal-sea-turtle-secrets",
    items: [
      { label: "Research", id: "research" },
      { label: "Rehabilitation", id: "rehabilitation" },
      { label: "Partnerships", id: "partnerships" },
      { label: "Investing in sea turtles", id: "investing" },
      { label: "Sponsor a sea turtle", id: "sponsor" },
    ],
  },
  {
    heading: "Fraser Coast's sea turtles",
    headingId: "turtles-overview",
    items: [
      { label: "Leatherback turtle", id: "leatherback-turtle" },
      { label: "Green turtle", id: "green-turtle" },
      { label: "Loggerhead turtle", id: "loggerhead-turtle" },
      { label: "Flatback turtle", id: "flatback-turtle" },
      { label: "Hawksbill turtle", id: "hawksbill-turtle" },
      { label: "Olive ridley turtle", id: "olive-ridley-turtle" },
    ],
  },
  {
    heading: "See inside the UniSC Milbi Centre",
    headingId: "inside-the-centre",
    items: [
      { label: "Saving our sea turtles", id: "saving-our-sea-turtles" },
      { label: "Revealing sea turtle secrets", id: "knowledge" },
    ],
  },
  {
    heading: "Three Butchulla lores",
    headingId: "butchulla-lores",
    items: [
      { label: "Butchulla care for land, sea and sky Country", id: "butchulla-care-for-land" },
      { label: "Culture, community and conservation", id: "butchulla-culture-community" },
      { label: "Butchulla care for country", id: "butchulla-care-for-country" },
      { label: "Dayman Point and Dayman Park", id: "dayman-point" },
    ],
  },
];

const NAV_SECTION_IDS = NAV_GROUPS.flatMap((group) => [
  group.headingId,
  ...group.items.map((item) => item.id),
]);

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuMounted, setIsMenuMounted] = useState(false);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string>(NAV_SECTION_IDS[0] ?? "");
  const menuScrollCleanupRef = useRef<(() => void) | null>(null);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    setIsMenuMounted(true);
    window.requestAnimationFrame(() => {
      setIsMenuOpen(true);
    });
  }, []);

  const toggleMenu = useCallback(() => {
    if (isMenuOpen) {
      closeMenu();
      return;
    }

    openMenu();
  }, [closeMenu, isMenuOpen, openMenu]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [closeMenu]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    return () => {
      menuScrollCleanupRef.current?.();
      menuScrollCleanupRef.current = null;
      document.documentElement.classList.remove("instant-scroll");
    };
  }, []);

  // Keep the menu mounted only while open or while its close animation runs.
  // iOS can keep tinting the bottom browser bar from an off-screen fixed layer,
  // so we fully unmount the fullscreen menu after the slide-out completes.
  useEffect(() => {
    if (isMenuOpen || !isMenuMounted) return;

    const timer = window.setTimeout(() => setIsMenuMounted(false), 300);
    return () => window.clearTimeout(timer);
  }, [isMenuMounted, isMenuOpen]);

  useEffect(() => {
    const handlePreviewFullscreen = (event: { isFullscreen: boolean }) => {
      setIsPreviewFullscreen(event.isFullscreen);
    };

    (emitter as any).on("preview-fullscreen", handlePreviewFullscreen);

    return () => {
      (emitter as any).off("preview-fullscreen", handlePreviewFullscreen);
    };
  }, []);

  useEffect(() => {
    let rafId = 0;

    const updateActiveSection = () => {
      rafId = 0;

      const header = document.querySelector('[data-fixed-header="true"]') as HTMLElement | null;
      const headerHeight = header?.offsetHeight ?? 0;
      const triggerY = headerHeight + 120;

      let nextActiveSectionId = NAV_SECTION_IDS[0] ?? "";

      for (const id of NAV_SECTION_IDS) {
        const section = document.getElementById(id);
        if (!section) continue;

        if (section.getBoundingClientRect().top <= triggerY) {
          nextActiveSectionId = id;
        } else {
          break;
        }
      }

      setActiveSectionId((current) =>
        current === nextActiveSectionId ? current : nextActiveSectionId
      );
    };

    const requestActiveSectionUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();

    window.addEventListener("scroll", requestActiveSectionUpdate, { passive: true });
    window.addEventListener("resize", requestActiveSectionUpdate);
    window.addEventListener("hashchange", requestActiveSectionUpdate);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("scroll", requestActiveSectionUpdate);
      window.removeEventListener("resize", requestActiveSectionUpdate);
      window.removeEventListener("hashchange", requestActiveSectionUpdate);
    };
  }, []);

  const handleScrollTo = (id: string) => {
    const target = document.getElementById(id);
    if (!target) return;

    menuScrollCleanupRef.current?.();
    menuScrollCleanupRef.current = null;
    setActiveSectionId(id);
    closeMenu();

    let timeoutId = 0;

    timeoutId = window.setTimeout(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);

    menuScrollCleanupRef.current = () => {
      window.clearTimeout(timeoutId);
    };
  };

  const headerZClass = isPreviewFullscreen ? "z-[100]" : "z-[1100]";
  const backdropZClass = isPreviewFullscreen ? "z-[85]" : "z-[1085]";
  const navZClass = isPreviewFullscreen ? "z-[95]" : "z-[1095]";

  return (
    <>
      <header data-fixed-header="true" className={`fixed top-0 left-0 right-0 ${headerZClass} w-full bg-white h-[100px] flex items-center`}>
        <div className="w-full px-5 sm:px-8 md:px-10 2xl:px-[39px] flex items-center justify-between">
          <img
            src={MilbiLogo}
            width={378}
            height={135}
            alt="Milbi Centre - Sea Turtle Research and Rehabilitation"
            className="h-[50px] w-auto"
          />
          <div className="relative z-[60]">
            <button
              type="button"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
              onClick={toggleMenu}
              className="relative -m-2 h-12 w-12 sm:h-12 sm:w-12 md:h-12 md:w-12 2xl:h-[52px] 2xl:w-[52px] flex items-center justify-center transition-opacity [@media(hover:hover)]:hover:opacity-70 [@media(hover:none)]:active:opacity-70 [@media(pointer:coarse)]:active:opacity-70"
            >
              <span
                className={`absolute block h-[2px] w-[26px] sm:w-[28px] md:w-[30px] 2xl:w-[34px] bg-black origin-center transition-transform duration-200 ease-out ${
                  isMenuOpen ? "rotate-45 translate-y-0" : "-translate-y-[7px] md:-translate-y-[8px]"
                }`}
              />
              <span
                className={`absolute block h-[2px] w-[26px] sm:w-[28px] md:w-[30px] 2xl:w-[34px] bg-black origin-center transition-opacity duration-150 ${
                  isMenuOpen ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`absolute block h-[2px] w-[26px] sm:w-[28px] md:w-[30px] 2xl:w-[34px] bg-black origin-center transition-transform duration-200 ease-out ${
                  isMenuOpen ? "-rotate-45 translate-y-0" : "translate-y-[7px] md:translate-y-[8px]"
                }`}
              />
            </button>
          </div>
        </div>
      </header>
      <div className="h-[100px]" />
      {isMenuMounted && (
        <>
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={closeMenu}
            className={`fixed inset-0 ${backdropZClass} bg-black/20 transition-opacity duration-300 ${
              isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
          />
          <nav
            className={`fixed right-0 top-0 ${navZClass} h-dvh w-full md:max-w-[595px] border-l border-black/10 bg-[rgba(255,255,255,0.95)] pl-5 pr-5 sm:pl-7 sm:pr-6 md:pl-10 md:pr-8 2xl:pl-[80px] 2xl:pr-[40px] pt-[116px] pb-16 sm:pb-20 md:pb-24 2xl:pb-[125px] overflow-y-auto transform transition-transform duration-300 ease-out pointer-events-auto ${
              isMenuOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="flex flex-col gap-7 sm:gap-8 md:gap-10 2xl:gap-[50px]">
              {NAV_GROUPS.map((group) => (
                <div key={group.heading} className="flex flex-col gap-2 sm:gap-2.5 md:gap-3 2xl:gap-[18px]">
                  <button
                    type="button"
                    onClick={() => handleScrollTo(group.headingId)}
                    className="text-left text-[18px] sm:text-[19px] md:text-[20px] 2xl:text-[22px] font-bold leading-[1.1] text-black"
                  >
                    {group.heading}
                  </button>
                  <ul className="flex flex-col gap-2 sm:gap-2.5 md:gap-3 2xl:gap-[18px]">
                    {group.items.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => handleScrollTo(item.id)}
                          className={`w-full pl-3 sm:pl-3.5 md:pl-4 2xl:pl-[18px] text-left text-[15px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] leading-[1.1] transition-colors ${
                            item.id === activeSectionId
                              ? "font-bold text-[#0052C4]"
                              : "font-normal text-black hover:text-black/70"
                          }`}
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
        </>
      )}
    </>
  );
}

// Hero Section - "Above the line"
export function HeroSection() {
  return (
    <section
      className="relative overflow-hidden flex flex-col justify-center h-[calc(100dvh-100px)] min-h-[400px]"
      style={{
        backgroundImage: `url(${HeroBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* <div className="absolute inset-0 bg-black/20 pointer-events-none" /> */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 70%)",
          opacity: 0.3,
        }}
      />
      {/* Content container */}
      <div className="absolute inset-0 z-10 flex flex-col justify-between px-5 sm:px-8 md:px-12 2xl:px-[60px] py-8 sm:py-12 md:py-16 2xl:py-20">
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            {/* Title */}
            <h1
              className="text-[46px] sm:text-[60px] md:text-[70px] 2xl:text-[80px] font-semibold text-white leading-none"
            >
              UniSC Milbi Centre:
            </h1>
            {/* Subtitle */}
            <p
              className="text-[24px] sm:text-[28px] md:text-[36px] 2xl:text-[40px] font-medium text-white leading-[1.2]"
            >
              Sea turtle research and <br />rehabilitation
            </p>
          </div>
        </div>
        {/* Notice - anchored to bottom */}
        <p className="text-[14px] sm:text-[15px] md:text-[16px] 2xl:text-[18px] font-normal text-white leading-[1.2] max-w-[400px] sm:max-w-[480px] md:max-w-[540px] 2xl:max-w-[582px]">
          This is a working research and rehabilitation centre dedicated to the treatment and recovery of marine wildlife. To minimise stress to animals in care and maintain strict biosecurity standards, public entry is not permitted.
        </p>
      </div>
    </section>
  );
}

// Intro Section - Research & Rehabilitation
export function IntroSection() {
  return (
    <section
      id="research"
      className="bg-[#0052C4] py-12 sm:py-16 md:py-20 2xl:py-[120px] px-6 sm:px-10 md:px-20 2xl:px-[155px]"
    >
      <div className="max-w-[1130px] mx-auto flex flex-col md:flex-row justify-between gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px]">
        {/* Left Column - Research */}
        <div className="w-full md:w-1/2 flex flex-col gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px]">
          <h2
            className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-white leading-none"
          >
            Research reveals sea turtle secrets
          </h2>
          <div
            className="flex flex-col gap-4 sm:gap-[17px] text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-[#EFEFEF] font-light leading-[1.2]"
          >
            <p>
            Sea turtles (milbi) live a mysterious life – we are still learning new things about turtles’ bodies, behaviour and journeys.
            </p>
            <p>
            People can threaten sea turtles’ safety and habitats through fishing, boating, plastic and light pollution, nest disturbances, degraded feeding grounds, and increased spread of disease.
            </p>
            <p>
            By combining knowledge from researchers, Butchulla People, volunteers and rangers, we can find answers about how best to rehabilitate and conserve milbi and their habitats.
            </p>
          </div>
        </div>

        {/* Right Column - Rehabilitation */}
        <div className="w-full md:w-1/2 flex flex-col gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px]">
          <h2
          id="rehabilitation"
            className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-white leading-none"
          >
            Rehabilitation saves sea turtles
          </h2>
          <div
            className="flex flex-col gap-4 sm:gap-[17px] text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-[#EFEFEF] font-light leading-[1.2]"
          >
            <p>
            When sea turtles are sick or injured, trained volunteers and rangers rescue them from beaches and waterways and bring them to the UniSC Milbi Centre. The sea turtles are checked by vets, carefully treated and put into specialised tanks to recover.
            </p>
            <p>
            The pattern of facial scales on each turtle is documented, and the turtles are microchipped before they are released so their movements can be tracked.
            </p>
            <p>
            We always want to hear about any turtle you see – sick, injured or in distress – call the Turtles in Trouble Rescue Inc. hotline on 0493 242 903.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Import partnership assets
import LogoUniSC from "../assets/microsite/logo-unisc.svg";
import LogoBNTAC from "../assets/microsite/logo-bntac.svg";
import LogoTITR from "../assets/microsite/logo-titr.svg";
import PartnershipUniSC from "../assets/microsite/partnership-unisc.webp";
import PartnershipTITR from "../assets/microsite/partnership-titr.webp";
import ButchullaNativeTitleVideo from "../assets/microsite/butchulla-native-title.mp4";
import ButchullaNativeTitlePoster from "../assets/microsite/butchulla-native-title.webp";
import ButchullaCarePoster from "../assets/microsite/butchulla-care.webp";
import ButchullaCareForCountryPoster from "../assets/microsite/butchulla-care-for-country.webp";

type Partner = {
  logo: string;
  name: string;
  media: {
    type: "image" | "video";
    src: string;
    poster?: string;
  };
  paragraphs: ComponentChildren[];
};

function PartnerTextLink({
  href,
  children,
}: {
  href: string;
  children: ComponentChildren;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium underline underline-offset-[2px] transition-opacity hover:opacity-80"
    >
      {children}
    </a>
  );
}

// Partnerships Section
export function PartnershipsSection() {
  const partners: Partner[] = [
    {
      logo: LogoUniSC,
      name: "University of the Sunshine Coast",
      media: {
        type: "image",
        src: PartnershipUniSC,
      },
      paragraphs: [
        <>
          The centre is a collaborative project led by the{" "}
          <PartnerTextLink href="https://www.facebook.com/unisunshinecoast?__cft__%255B0%255D=AZbnvRqg9QYTQNLtO2Y63-xQlVCOjm1X2pmh4__Xxkm10in0XZkZlwc5w95k9yaYRKNp23FjM2ut5Vh1ddyB9JNSRhmKXE_JcRxmrqc2MjC033adqXoNnam7ksDfp1PzHvNR9tL7Fopy8eTWyPiygoCNJ7U4FK4iLYIT30dXG1e5kLESL4ebzN2sIeO2J2BeIOGna4gpCEOq2XCsKJ5PEZqO">
            University of the Sunshine Coast
          </PartnerTextLink>{" "}
          (UniSC) in close partnership with the Butchulla Native Title
          Aboriginal Corporation (BNTAC) and Turtles In Trouble Rescue Inc.
          (TITR).
        </>,
        "Partnerships like this connect informed conservation with broader regional efforts to protect and care for sea turtles.",
        <>
          <PartnerTextLink href="https://www.unisc.edu.au/community/giving-to-unisc/sea-turtle-rehabilitation-and-research">
            Turtle research at UniSC
          </PartnerTextLink>{" "}
          is led by{" "}
          <PartnerTextLink href="https://www.unisc.edu.au/staff/professor-kathy-townsend#biography">
            marine biologist Professor Kathy Townsend.
          </PartnerTextLink>
        </>,
      ],
    },
    {
      logo: LogoBNTAC,
      name: "Butchulla Native Title Aboriginal Corporation (BNTAC)",
      media: {
        type: "video",
        src: ButchullaNativeTitleVideo,
        poster: ButchullaNativeTitlePoster,
      },
      paragraphs: [
        "The milbi (sea turtle) is one of the animals that Butchulla People have a cultural connection with on their Country. Butchulla People’s connection to Country is interwoven with Land, Sea and Sky.",
        "BNTAC provides cultural guidance and advice to the centre. BNTAC also has a Djaa Narawi (Land & Sea) Ranger providing first-hand care to sick and injured milbi. ",
        "BNTAC works in partnership with UniSC to ensure that the activities and practices carried out in the name of Traditional Owners are respectful and align with our cultural beliefs, practices and protocols.",
      ],
    },
    {
      logo: LogoTITR,
      name: "Turtles In Trouble Rescue Inc.",
      media: {
        type: "image",
        src: PartnershipTITR,
      },
      paragraphs: [
        "Turtles in Trouble Rescue (TITR) is a not-for-profit group of volunteers and citizen scientists who help rescue, rehabilitate, release and conserve sea turtles and their marine environment.",
        "TITR emerged in 2021 because people were finding more and more unwell sea turtles on the Fraser Coast after major back-to-back floods.",
        "In just 4 years, TITR volunteers have responded to more than 1400 call outs and rescued over 350 turtles.",
        "This centre helps TITR better assist with sick and injured turtles by providing more immediate first-aid care and less travel time for turtles to other centres.",
        <>
          You are welcome to volunteer with TITR: get in touch{" "}
          <PartnerTextLink href="https://turtlesintroublerescue.org.au/#volunteer">
            here.
          </PartnerTextLink>
        </>,
      ],
    },
  ];

  return (
    <section id="partnerships" className="section-lazy bg-white py-12 sm:py-16 md:py-20 2xl:py-[120px] px-6 sm:px-10 md:px-20 2xl:px-[155px]">
      <div className="flex flex-col gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px] w-full max-w-[1130px] mx-auto">
        {/* Section Title */}
        <h2
          className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"
        >
          A centre built on partnerships
        </h2>

        {/* Partner Cards */}
        {partners.map((partner, index) => (
          <div
            key={index}
            className="flex flex-col gap-6 sm:gap-7 md:gap-8 2xl:gap-[40px]"
          >
            {/* Logo and Name */}
            <div className="flex flex-col gap-2 sm:gap-3 md:gap-4 2xl:gap-[20px]">
              <img
                src={partner.logo}
                alt={`${partner.name} logo`}
                className="w-[80px] h-[80px] sm:w-[100px] sm:h-[100px] md:w-[120px] md:h-[120px] object-contain"
              />
              <h3
                className="text-[18px] sm:text-[20px] md:text-[21px] 2xl:text-[22px] font-medium text-black leading-[1.2]"
              >
                {partner.name}
              </h3>
            </div>

            {/* Paragraphs and Image */}
            <div className="flex flex-col gap-6 sm:gap-7 md:gap-8 2xl:gap-[40px]">
              {/* Paragraphs */}
              <div
                className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-black font-light leading-[1.2]"
                
              >
                {partner.paragraphs.map((paragraph, pIndex) => (
                  <p key={pIndex}>{paragraph}</p>
                ))}
              </div>

              <div
                className="w-full overflow-hidden max-h-[400px] sm:max-h-[500px] md:max-h-[622px]"
                style={{ aspectRatio: "1130 / 622" }}
              >
                {partner.media.type === "video" ? (
                  <video
                    src={partner.media.src}
                    poster={partner.media.poster}
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={partner.media.src}
                    alt={partner.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Sponsor Section
export function SponsorSection() {
  return (
    <section id="sponsor" className="section-lazy bg-[#0052C4] py-12 sm:py-16 md:py-20 2xl:py-[120px] px-6 sm:px-10 md:px-20 2xl:px-[155px]">
      <div className="flex flex-col items-start gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px] w-full max-w-[1130px] mx-auto">
        {/* Title */}
        <h2
          className="w-full text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-semibold text-white leading-[1.2] text-left"
        >
          Sponsor a sea turtle, their snacks, or first aid kits
        </h2>

        {/* Intro paragraphs */}
        <div
          className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-white font-normal leading-[1.5] w-full"
        >
          <p>
          Sea turtles heal slowly. Our turtle patients come in to the centre with all kinds of illnesses and injuries that can take from 2 months to 2 years to treat.
          </p>
          <p>
          A sea turtle’s medical bills can be huge: it can cost an average of $10,000 to rehabilitate one sick sea turtle. The centre can cost up to $500,000 per year to run.
          </p>
          <p>
            Even the smallest <a href="https://payments.usc.edu.au/95Q/tran?UDS_ACTION_DATA=bV1XAjdCKHdOWVYBQEJDBFlRQnRPXEwbXjY3XXY3IzEANlAh" target="_blank" rel="noopener noreferrer" className="font-medium underline">donations</a> can help take a turtle from sick to a rescued-and-released success story. You and your friends could sponsor:
          </p>
        </div>

        {/* Donation options */}
        <ul
          className="w-full list-disc pl-4 marker:text-[0.7em] text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-white font-normal leading-[1.5] md:columns-2 md:gap-[60px]"
        >
          <li className="break-inside-avoid">a sea turtle snack for $10</li>
          <li className="break-inside-avoid">feeding a turtle for a day for $25</li>
          <li className="break-inside-avoid">turtle care for a day for $50</li>
          <li className="break-inside-avoid">turtle toy to make their stay more enjoyable for $100</li>
          <li className="break-inside-avoid">housing a turtle for a week for $250</li>
          <li className="break-inside-avoid">a new patient vet check for $500</li>
          <li className="break-inside-avoid">satellite tracking tag for $5500</li>
        </ul>
      </div>
    </section>
  );
}

// Import decorative assets
import TurtleDeco from "../assets/turtle-deco-large.webp";
import TurtleDecoMobile from "../assets/microsite/turtle-deco-mobile.png";
import TurtleGreenInvesting from "../assets/turtle-green-investing.webp";

// Investing Section
export function InvestingSection() {
  return (
    <section id="investing" className="section-lazy relative bg-[#F2F2F2] py-12 sm:py-16 md:py-20 2xl:py-[120px] px-6 sm:px-10 md:px-20 2xl:px-[155px] overflow-hidden">
      {/* Decorative turtle illustrations */}
      <img
        src={TurtleDeco}
        width={2880}
        height={1562}
        alt=""
        className="absolute pointer-events-none opacity-100 inset-0 object-cover object-left h-full w-full hidden md:block"
      />
      <img
        src={TurtleDecoMobile}
        width={2880}
        height={1562}
        alt=""
        className="absolute pointer-events-none opacity-100 top-[-60px] sm:top-[-80px] right-[-18px] max-w-[230px] w-full sm:max-w-[300px] h-auto object-contain md:hidden"
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-start gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px] w-full max-w-[1130px] mx-auto">
        {/* Title */}
        <h2
          className="w-full max-w-[854px] text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"
        >
          Investing in sea turtles improves our science, culture and communities
        </h2>
        <div className="flex w-full gap-12 md:flex-row flex-col-reverse justify-center items-center">
          <div className="max-w-[575px] w-full h-auto -ml-12 px-5">
            {/* Green turtle photo */}
            <img
              src={TurtleGreenInvesting}
              width={4096}
              height={2660}
              alt="Green sea turtle"
              className="pointer-events-none "
            />
          </div>
          {/* Paragraphs - aligned right */}
          <div
            className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-black font-light leading-[1.2] w-full "
          >
            <p>
            Sea turtles are living mirrors of ocean health – what we find in their bodies, from plastics to pollutants, reveals what they have been exposed to in the sea.
            </p>
            <p>
            This ‘warning system’ in turtles means they are a ‘sentinel species’, showing us if things are going wrong with the environment due to pollution or climate change.
            </p>
            <p>
            The more researchers can find out about sea turtles, the more we can understand how to protect these endangered animals.
            </p>
            <p>
            Investment in the centre by UniSC, Traditional Owners, volunteers, governments and the community brings people, culture, knowledge and science together to protect and care for turtles and their fragile ocean habitats.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Import supporters image
import SupportersImage from "../assets/microsite/supporters-image.webp";
import TurtlesOverviewBg from "../assets/microsite/turtles-overview-bg.webp";

// Supporters Section
export function SupportersSection() {
  return (
    <section id="supporters" className="section-lazy bg-white py-12 sm:py-16 md:py-20 2xl:py-[120px] px-6 sm:px-10 md:px-20 2xl:px-[155px] ">
      <div className="w-full max-w-[1130px] mx-auto flex flex-col md:flex-row items-start md:items-center gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px]">
        <div className="w-full md:w-1/2 min-w-0 flex flex-col gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px]">
          {/* Title */}
          <h2
            className="w-full text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"
            
          >
            Milbi Centre supporters, we thank you
          </h2>

          {/* Paragraphs - limited width to avoid overlapping with image */}
          <div
            className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-black font-light leading-[1.2] "
            
          >
            <p>
            The centre was established by UniSC through a $1.29 million commitment from the Queensland Government, $250,000 from the Australian Government and through strong partnerships with the Fraser Coast Regional Council, the Member for Hinkler, Queensland Parks and Wildlife Service, and philanthropic donations (Sandy Michell Legacy Fund, Harris Estate Charitable Foundation, Australian Marine Conservation Society and individual donors).
            </p>
            <p>
            Australia Zoo Wildlife Hospital, SeaWorld Foundation, SeaLife Mooloolaba and Quoin Island Turtle Rehabilitation Centre provided expert advice for establishing the centre.
            </p>
          </div>
        </div>
        {/* Image column */}
        <div className="w-full md:w-1/2 min-w-0 flex justify-center ">
          <img
            src={SupportersImage}
            width={876}
            height={876}
            alt="Milbi Centre supporters"
            className="w-full max-w-[438px] h-auto pointer-events-none"
          />
        </div>

      </div>
    </section>
  );
}

// Turtles Overview Section
export function TurtlesOverviewSection() {
  return (
    <section
      id="turtles-overview"
      className="section-lazy relative py-12 sm:py-16 md:py-20 2xl:py-[120px] px-6 sm:px-10 md:px-20 2xl:px-[155px] overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${TurtlesOverviewBg})` }}
    >
      <div className="relative z-10 flex flex-col gap-8 sm:gap-9 md:gap-[48px] 2xl:gap-[60px] w-full max-w-[1130px] mx-auto">
        <h2
          className="text-[32px] sm:text-[44px] md:text-[56px] 2xl:text-[80px] font-semibold text-black leading-[1.2]"
        >
          Fraser Coast’s sea turtles – residents and visitors
        </h2>

        <div
          className="flex flex-col gap-[18px] sm:gap-[22px] md:gap-[28px] 2xl:gap-[40px] text-[16px] sm:text-[18px] md:text-[20px] 2xl:text-[24px] text-black font-medium leading-[1.2] max-w-[1130px]"
          
        >
          <p>
          Here on the Fraser Coast, we can see 6 of the 7 sea turtles found on Earth.
          </p>
          <p>
          The most common sea turtle sightings are the green, loggerhead and hawksbill turtles, who live in seagrass meadows, rocky outcrops and coral reefs. 
          </p>
          <p>
          Green and loggerhead turtles can travel hundreds of kilometres to nest on the beaches of the Fraser Coast and K’gari.
          </p>
          <p>
          Leatherback, olive ridley and flatback sea turtles visit us from time to time as they pass through the area in search of a good feed. The flatback turtle is only found in Australia; but like the olive ridley, the Fraser Coast marks the southern limit of their range, and they are only occasional visitors.
          </p>
        </div>
      </div>
    </section>
  );
}

// Inside The Centre Section - Hero-style with centered title
export function InsideTheCentreSection() {
  return (
    <section
      id="inside-the-centre"
      className="section-lazy relative overflow-hidden px-4 sm:px-6 md:px-8 2xl:px-[50px] py-16 sm:py-24 md:py-28 2xl:py-[150px] h-[calc(100dvh-100px)] max-h-[1000px] min-h-[420px] flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${SeeInsideBg})` }}
    >
      <div className="relative z-10 w-full max-w-[1130px] mx-auto flex items-center justify-center">
        <h2
          className="w-full text-center text-black font-semibold leading-[1.2] text-[32px] sm:text-[44px] md:text-[56px] 2xl:text-[80px] "
          
        >
          See inside the UniSC
          <br />
          Milbi Centre
        </h2>
      </div>
    </section>
  );
}

// Saving Turtles Section - Comprehensive conservation content
export function SavingTurtlesSection() {
  const sectionTitleClass =
    "text-[20px] sm:text-[24px] md:text-[26px] 2xl:text-[28px] font-medium text-black leading-[1.2]";
  const bodyClass =
    "text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] font-light text-black leading-[1.2]";

  return (
    <section
      id="saving-our-sea-turtles"
      className="section-lazy bg-white py-12 sm:py-16 md:py-20 2xl:py-[120px] px-4 sm:px-6 md:px-8 2xl:px-[50px]"
    >
      <div className="flex flex-col gap-9 sm:gap-12 md:gap-[60px] 2xl:gap-[80px] w-full max-w-[1130px] mx-auto">
        <h2
          className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"
          
        >
          Saving our sea turtles
        </h2>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Turtles In Trouble Rescue Inc. rescue local sea turtles
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3" >
            <p className={bodyClass}>
            Turtles In Trouble Rescue Inc. (TITR) volunteers respond to reports of stranded, sick, injured or unwell turtles on the Fraser Coast.
            </p>
            <p className={bodyClass}>
            Volunteers rescue and transport distressed turtles into care at the UniSC Milbi Centre, assist with transfer to veterinary facilities, and support their eventual release back into the ocean.
            </p>
            <p className={bodyClass}>
            Volunteers are active in monitoring, conservation research, community education and outreach to help protect turtles and their habitats.
            </p>
          </div>
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            <img
              src={SavingTurtles1}
              width={1128}
              height={621}
              alt="Turtles In Trouble Rescue Inc. volunteers assisting turtle rescue efforts"
              className="w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
            />
            <p className="text-black text-[12px] font-light leading-[1.2em] italic">Photo by TITR</p>
          </div>
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Djaa Narawi Rangers protect turtles (milbi)
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3" >
            <p className={bodyClass}>
            Butchulla Djaa Narawi (Land and Sea) Rangers are entrusted with protecting milbi located on Butchulla Country. Rangers are involved in all aspects of caring and protecting milbi, including monitoring nesting sites, ensuring milbi eggs are protected from predators and people, conserving milbi habitats, and making sure any sick or injured milbi are treated and able to return to Sea Country.
            </p>
            <p className={bodyClass}>
            Rangers work in partnership with non-government organisations, including Turtles In Trouble Rescue Inc., to monitor milbi health and serve as frontline responders to help rescue any sick or injured milbi.
            </p>
            <p className={bodyClass}>
            If any milbi requires hospitalisation and treatment away from Country, rangers are always on hand to welcome the milbi back to Butchulla Country. Rangers want to make sure milbi are released in full health to continue living in their Sea Country.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <img
              src={SavingTurtles21}
              width={1110}
              height={1244}
              alt="Djaa Narawi Rangers protecting milbi on Butchulla Country"
              className="w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
            />
            <img
              src={SavingTurtles22}
              width={1110}
              height={1242}
              alt="Djaa Narawi Rangers caring for milbi on Butchulla Country"
              className="w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Report to rescue to release: step by step
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <img src={SavingTurtles3} width={1071} height={530} alt="Sea turtle report stage" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
                <p
                  aria-hidden="true"
                  className="hidden text-black text-[12px] font-light leading-[1.2em] italic md:block md:invisible"
                >
                  Photo by TITR
                </p>
              </div>
              <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
                <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">1. Report</h4>
                <div className="flex flex-col gap-2 sm:gap-3">
                  <p className={bodyClass}>Quick reporting gives turtles the best chance of survival.</p>
                  <p className={bodyClass}>
                  If you see a sick, injured, entangled or stranded sea turtle near Hervey Bay or the Fraser Coast, call TITR. on 0493 242 903. Do not move the turtle.
                  </p>
                  <p className={bodyClass}>
                  You can also report sightings of other marine animals to the Queensland Department of the Environment, Tourism, Science and Innovation (DETSI) anywhere along the Queensland coast by using the {" "}
                    <a href="https://play.google.com/store/apps/details?id=gov.qld.dnrme.QWildlife&hl=en_AU" target="_blank" rel="noopener noreferrer" className="font-medium underline">QWildlife </a>{" "}
                    app or by calling 1300 130 372.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <img src={SavingTurtles4} width={1070} height={530} alt="Sea turtle rescue stage" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
                <p className="text-black text-[12px] font-light leading-[1.2em] italic">Photo by TITR</p>
              </div>
              <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
                <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">2. Rescue</h4>
                <div className="flex flex-col gap-2 sm:gap-3">
                  <p className={bodyClass}>
                  Trained TITR volunteers and Land and Sea Rangers will respond to your call and do an initial health assessment of an injured or sick turtle. If it needs rescuing, they will safely retrieve the turtle from the beach or water’s edge, taking care to reduce stress and prevent further harm.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <img src={SavingTurtles5} width={1071} height={530} alt="Sea turtle response and triage stage" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
                <p
                  aria-hidden="true"
                  className="hidden text-black text-[12px] font-light leading-[1.2em] italic md:block md:invisible"
                >
                  Photo by TITR
                </p>
              </div>
              <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
                <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">3. Respond</h4>
                <div className="flex flex-col gap-2 sm:gap-3">
                  <p className={bodyClass}>
                  The volunteers will bring the sea turtle to our centre where a veterinary examination will diagnose injuries or illness and begin care. Turtles requiring surgery or critical care will be transferred to Australia Zoo Wildlife Hospital.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <img src={SavingTurtles6} width={1070} height={530} alt="Sea turtle rehabilitation stage" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
                <p
                  aria-hidden="true"
                  className="hidden text-black text-[12px] font-light leading-[1.2em] italic md:block md:invisible"
                >
                  Photo by TITR
                </p>
              </div>
              <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
                <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">4. Rehabilitation</h4>
                <div className="flex flex-col gap-2 sm:gap-3">
                  <p className={bodyClass}>
                  Under expert supervision in our centre, turtles receive ongoing treatment such as medication, basic wound care, nutrition and rehabilitation in specialist tanks. Recovery can take weeks or months to years, depending on how badly affected the turtle is.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <img src={SavingTurtles7} width={1110} height={530} alt="Sea turtle release stage" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
                <p
                  aria-hidden="true"
                  className="hidden text-black text-[12px] font-light leading-[1.2em] italic md:block md:invisible"
                >
                  Photo by TITR
                </p>
              </div>
              <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
                <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">5. Release</h4>
                <div className="flex flex-col gap-2 sm:gap-3">
                  <p className={bodyClass}>
                    Once fully recovered and strong enough to survive at sea, trained TITR. volunteers and Land and Sea Rangers will release the turtle back into the ocean – usually near where it was first found – ready to continue its life in the wild.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <img src={SavingTurtles8} width={1110} height={530} alt="Sea turtle release celebration" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
              </div>
            </div>
          </div>
        </div>
       

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Watch sea turtle care in action
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3" >
            <p className={bodyClass}>Sick or injured turtles are taken for specialist vet checks, treatment and care inside our centre.</p>
            <p className={bodyClass}>After we treat them, the sea turtles are rehabilitated and strengthened, ready for eventual release.</p>
            <p className={bodyClass}>
            If a sea turtle needs more care than the UniSC Milbi Centre can provide, we take them to larger specialised facilities like the Australia Zoo Wildlife Hospital.
            </p>
          </div>
          
          <div className="w-full overflow-hidden" style={{ aspectRatio: "1920 / 1080" }}>
            <video
              className="w-full h-full object-cover"
              poster={WatchSeaTurtleCarePoster}
              controls
              playsInline
              preload="metadata"
            >
              <source src={WatchSeaTurtleCareVideo} type="video/mp4" />
            </video>
          </div>
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Meet our rescue turtles
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]" >
            <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
              <img
                src={MeetRescue1}
                width={700}
                height={454}
                alt="Sango the rescued green turtle"
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
              <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">Saving Sango twice</h4>
              <div className="flex flex-col gap-2 sm:gap-3">
                <p className={bodyClass}>
                Imagine carrying 1 kg of muck, mud and broken bone in a deep shell wound! This was the fate of Sango, a female green turtle, after her rescue in 2022. She was likely hit by a boat propeller.
                </p>
                <p className={bodyClass}>After 3 months of treatment and rehab, Sango was finally released back into the wild.</p>
                <p className={bodyClass}>
                Just over a year later, TITR found Sango again, stranded with signs of soft-shell syndrome. After another 3 months of treatment, she was released again.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
              <img
                src={MeetRescue2}
                width={700}
                height={454}
                alt="Loggerhead turtle rescue story"
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
              <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">Log a loggerhead</h4>
              <div className="flex flex-col gap-2 sm:gap-3">
                <p className={bodyClass}>
                <span className="italic">Just after New Year’s 2025, it’s sunrise and the tide is out on Sirenia Beach, leaving a turtle stranded. A visitor calls TITR.</span>
                </p>
                <p className={bodyClass}>
                TITR rushed out to discover an underweight loggerhead with a partially healed, amputated front flipper.
                </p>
                <p className={bodyClass}>
                TOD, as it was named, was treated to reduce sandy grit compacting its gut and given a high-protein diet to rebuild its strength. TOD was released a month later with a satellite tag that showed it initially stayed near the release spot.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
              <img
                src={MeetRescue3}
                width={700}
                height={454}
                alt="Flatback turtle floater rescue story"
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
              <h4 className="text-[18px] sm:text-[20px] md:text-[22px] font-medium leading-[1.2] text-black">Rescuing a flatback floater</h4>
              <div className="flex flex-col gap-2 sm:gap-3">
                <p className={bodyClass}>
                A ‘floater’, or a sea turtle with floating syndrome, is unable to dive because of gas caused by eating plastic or marine rubbish, or from injury or infection.
                </p>
                <p className={bodyClass}>
                In late 2025, a local boatie found a floater off Burrum Heads. Volunteers, who named the turtle Pikelet, think his injuries were caused by a shark attack.
                </p>
                <p className={bodyClass}>
                Pikelet underwent treatment and rehab for 56 days before being released at Burrum Heads with about 50 community members wishing him luck for his onward journey.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Report any sighting of a sea turtle, sick or healthy, dead or alive
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3" >
            <p className={bodyClass}>
            Whether a turtle is swimming strongly, resting on the beach, injured or dead, your report will help build the knowledge we need to protect sea turtles for the future.
            </p>
            <p className={bodyClass}>
            If you see a sick, injured, entangled or stranded sea turtle near Hervey Bay or the Fraser Coast, call Turtles in Trouble Rescue. directly on 0493 242 903. Do not move the turtle. Quick reporting gives turtles the best chance of survival.
            </p>
            <p className={bodyClass}>
            You can also report sightings to Queensland Department of the Environment, Tourism, Science and Innovation (DETSI) by using the{" "}
              <a href="https://play.google.com/store/apps/details?id=gov.qld.dnrme.QWildlife&hl=en_AU" target="_blank" rel="noopener noreferrer" className="font-medium underline">QWildlife</a>{" "}
              app or by calling 1300 130 372.
            </p>
            <p className={bodyClass}>Reporting all turtle sightings helps researchers:</p>
            <ul className={`${bodyClass} list-disc pl-4 md:pl-6 marker:text-[0.7em]`}>
              <li>identify important turtle habitats</li>
              <li>map hotspots for injuries, strandings and deaths</li>
              <li>track individuals and populations over time</li>
              <li>detect impacts of climate change</li>
              <li>inform safer boating and fishing practices</li>
              <li>guide conservation planning and government protection measures</li>
            </ul>
          </div>
          <img src={ReportSighting} width={1130} height={622} alt="People documenting and reporting sea turtle sightings" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass}>
            Locals can get involved
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3">
            <p className={bodyClass}>
              Turtles in Trouble Rescue welcomes new volunteers. You can express interest {" "}
              <a href="https://play.google.com/store/apps/details?id=gov.qld.dnrme.QWildlife&hl=en_AU" target="_blank" rel="noopener noreferrer" className="font-medium underline">on their website</a>{" "}
              or send them an email at{" "}
              turtlesintroublefc@gmail.com
            </p>
            <p className={bodyClass}>
              And have you heard of citizen science? You can be a vital part of real research by monitoring wildlife or
              collecting data with professional scientists on the Fraser Coast:
            </p>
            <ul className={`${bodyClass} list-disc pl-4 md:pl-6 marker:text-[0.7em]`}>
              <li>Log turtle, fish and other marine animal sightings with{" "}
                <a href="https://www.frasercoast.qld.gov.au/Environment-and-Water/Natural-environment/Wildlife/Fish" target="_blank" rel="noopener noreferrer" className="font-medium underline">iNaturalist</a>
              </li>
              <li>Join a citizen science activity such as{" "}
                <a href="https://www.seagrasswatch.org/" target="_blank" rel="noopener noreferrer" className="font-medium underline">Seagrass Watch</a>{" "}
                and{" "}
                <a href="https://coralwatch.org/" target="_blank" rel="noopener noreferrer" className="font-medium underline">Coral Watch</a>
              </li>
              <li>Run or join a local rubbish clean up, and log rubbish data with the{" "}
                <a href="https://tangaroablue.org/database/" target="_blank" rel="noopener noreferrer" className="font-medium underline">Australian Marine Debris Initiative Database</a>
              </li>
              <li>Assist researchers by using the Pacific Whale Foundation&apos;s {" "}
                <a href="https://pacificwhale.org/what-we-do/research/learn-about-marine-life/whale-dolphin-tracker-live-sightins-map/" target="_blank" rel="noopener noreferrer" className="font-medium underline">Whale &amp; Dolphin Tracker</a>
              </li>
              <li>Monitor plants and wildlife to understand the impacts of climate change with{" "}
                <a href="https://www.climatewatch.org.au/species/marine" target="_blank" rel="noopener noreferrer" className="font-medium underline">ClimateWatch</a>
              </li>
              <li>Contribute to reef biodiversity monitoring with{" "}
                <a href="https://reeflifesurvey.com/how-to/" target="_blank" rel="noopener noreferrer" className="font-medium underline">Reef Life Survey</a>
              </li>
              <li>Log marine species through{" "}
                <a href="https://www.redmap.org.au/article/marine-citizen-science-in-australia/" target="_blank" rel="noopener noreferrer" className="font-medium underline">RedMap</a>{" "}
                and contribute to Australia&apos;s marine environment data.
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Watch out for the hatchlings
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3" >
            <p className={bodyClass}>A sea turtle&apos;s lifecycle is complicated.</p>
            <p className={bodyClass}>
            When turtle hatchlings emerge from their nests, they face one of the most dangerous journeys in nature.
            </p>
            <p className={bodyClass}>
            After hatching they follow the moonlight to the sea, crawling towards the lowest, brightest light on the horizon. Reducing artificial lights from houses, streets and cars can increase the chance the hatchlings will go in the right direction.
            </p>
            <p className={bodyClass}>
            As they make their way to the sea, the hatchlings face predators such as seagulls, dingoes, goannas, foxes, pigs and pets. Hopefully, they do not meet other obstacles such as 4WD tracks, damaged dunes or litter.
            </p>
            <p className={bodyClass}>Researchers estimate that only about 1 in 1,000 hatchlings survives to adulthood.</p>
            <p className={bodyClass}>
            They then travel from the beach they hatched on, to their feeding grounds, only to return to their nesting beach at around 30 years of age. 
            </p>
          </div>
          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <img src={WatchOutHatchlings} width={2260} height={1244} alt="Sea turtle hatchlings crossing the beach toward the ocean" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
            <div className="w-full overflow-hidden" style={{ aspectRatio: "2260 / 1270" }}>
              <video
                className="w-full h-full object-cover"
                poster={WatchOutHatchlings2Poster}
                controls
                playsInline
                preload="metadata"
              >
                <source src={WatchOutHatchlingsVideo} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass} >
            Tourists: continue your conservation actions back home
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3" >
            <p className={bodyClass}>You can help turtles anywhere, all the time:</p>
            <ul className={`${bodyClass} list-disc pl-4 md:pl-6 marker:text-[0.7em]`}>
              <li>don&apos;t litter, and pick up rubbish if you can</li>
              <li>recycle plastics</li>
              <li>refuse single-use plastics</li>
              <li>choose sustainable seafood</li>
              <li>promote climate action</li>
              <li>monitor beaches and rivers for distressed aquatic life</li>
              <li>report your wildlife sightings</li>
            </ul>
            <p className={bodyClass}>
              Global studies estimate that about half of all sea turtles have plastic inside them. Turtles often think
              floating plastic is food like jellyfish or algae. Plastic inside a turtle can starve them, choke them, drown
              them or change how they swim.
            </p>
          </div>
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            <img src={TouristsContinue} width={1130} height={622} alt="Beach conservation activity to reduce turtle threats from plastic" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
            <p className="text-black text-[12px] font-light leading-[1.2em] italic">
              All 205 pieces of rubbish were found in a single turtle
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
          <h3 className={sectionTitleClass}>
            Follow our released turtles around the globe
          </h3>
          <div className="flex flex-col gap-2 sm:gap-3">
            <p className={bodyClass}>
              Sea turtles have always been known as ocean explorers. Newer tagging technology has revealed just how far and
              wide they go.
            </p>
            <p className={bodyClass}>
              Information collected about turtles&apos; adventures improves our understanding of the world&apos;s oceans,
              their many unique habitats and why they face threats like dying in fishing grounds.
            </p>
            <p className={bodyClass}>
              You can follow our ocean explorers using the{" "}
              <a href="https://www.qld.gov.au/environment/plants-animals/animals/discovering-wildlife/marine-wildlife/turtles/turtle-tracking/research-program" target="_blank" rel="noopener noreferrer" className="font-medium underline">Queensland Government&apos;s loggerhead program</a>{" "}
              and the{" "}
              <a href="https://www.sunshinecoast.qld.gov.au/resources/tracking-sea-turtles" target="_blank" rel="noopener noreferrer" className="font-medium underline">Sunshine Coast Council&apos;s turtle map</a>.
            </p>
          </div>
          <img src={FollowReleasedTurtles} width={2260} height={1244} alt="Sea turtle tracking map and migration routes" className="w-full h-auto object-cover" loading="lazy" decoding="async" />
        </div>
      </div>
    </section>
  );
}

// Butchulla Section - Hero-style with centered title and AR button
interface ButchullaSectionProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  isChecking: boolean;
  isCompatible: boolean;
}

export function ButchullaSection({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  isChecking,
  isCompatible,
}: ButchullaSectionProps) {
  const handleLaunchAR = (selectedModel: ModelData) => {
    if (selectedModel.arType === "webxr" && isCompatible) {
      onLaunchAR(selectedModel);
    }
  };

  return (
    <section
      id={getModelHash(model)}
      className="section-lazy relative overflow-hidden px-4 sm:px-6 md:px-8 2xl:px-[50px] py-16 sm:py-24 md:py-28 2xl:py-[150px] h-[calc(100dvh-100px)] max-h-[1000px] min-h-[420px] sm:min-h-[520px] md:min-h-[640px] 2xl:min-h-[900px] flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${ButchullaPeopleBg})` }}
    >
      <div className="relative z-10 w-full max-w-[1170px] mx-auto flex flex-col items-center justify-center gap-8 sm:gap-10 md:gap-14 2xl:gap-[100px]">
        <h2
          className="w-full text-center max-w-[570px] sm:max-w-[670px] md:max-w-[870px] 2xl:max-w-[1170px] text-black font-semibold leading-[1.2] text-[32px] sm:text-[44px] md:text-[56px] 2xl:text-[80px] "
        >
          Butchulla People: an ancient and modern
          Milbi legacy
        </h2>

        {/* AR Button */}
        <div className="w-full flex justify-center">
          <ARButton
            model={model}
            onLaunchAR={handleLaunchAR}
            onTrackLaunchClick={onTrackLaunchClick}
            isChecking={isChecking}
            isCompatible={isCompatible}
            variant="outline"
          />
        </div>
      </div>
    </section>
  );
}

// Butchulla Lores Section - Three lores caring for Country
export function ButchullaLoresSection() {
  return (
    <section
      id="butchulla-lores"
      className="section-lazy overflow-hidden relative bg-[#003D92]"
    >
      {/* Background image overlay - positioned at top, ~1155px height */}
      <div
        className="absolute inset-x-0 top-0 h-[600px] sm:h-[800px] md:h-[1000px] 2xl:h-[1155px] bg-top bg-no-repeat pointer-events-none"
        style={{ backgroundImage: `url(${ButchullaLoresBg})`, backgroundSize: '100% 100%' }}
      />

      <div className="relative py-12 sm:py-16 md:py-20 2xl:py-[120px] px-6 sm:px-10 md:px-20 2xl:px-[155px]">
        <div className="flex flex-col gap-10 sm:gap-12 md:gap-[60px] 2xl:gap-[60px] w-full max-w-[1130px] mx-auto items-center">
          <div className="flex flex-col gap-8 md:gap-[40px] w-full max-w-[1130px]">
            <h2 className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-bold text-white leading-[1.2] tracking-[-0.02em]">
              Three Butchulla lores:
              <span className="block font-medium">caring for Country</span>
            </h2>
            <p className="text-[16px] sm:text-[18px] md:text-[20px] 2xl:text-[22px] font-normal text-white leading-[1.2] tracking-[-0.03em]">
              The three Butchulla lores (used with BNTAC permission):
            </p>
          </div>

          <img
            src={ButchullaLoresGraphic}
            width={1717}
            height={2261}
            alt="The three Butchulla lores in language and English"
            className="w-full max-w-[855px] h-auto object-contain"
            loading="lazy"
            decoding="async"
          />

          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px] w-full max-w-[1130px]">
            <div className="w-full overflow-hidden" style={{ aspectRatio: "2260 / 1272" }}>
              <video
                className="w-full h-full object-cover"
                poster={ButchullaLoresPoster}
                controls
                playsInline
                preload="metadata"
                aria-label="Joyce Bonner singing the three Butchulla lores"
              >
                <source src={ButchullaLoresVideo} type="video/mp4" />
              </video>
            </div>
            <p className="text-white text-[12px] font-light leading-[1.2em] italic">
              Joyce Bonner (Aunty Joy), respected Butchulla Elder and linguist singing the 3 lores.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Knowledge Section - Research and science content
export function KnowledgeSection() {
  const sectionTitleClass =
    "text-[20px] sm:text-[24px] md:text-[26px] 2xl:text-[28px] font-medium text-black leading-[1.2]";
  const bodyClass =
    "text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] font-light text-black leading-[1.2]";

  return (
    <section id="knowledge" className="section-lazy bg-[#F2F2F2] py-12 sm:py-16 md:py-20 2xl:py-[120px] px-4 sm:px-6 md:px-8 2xl:px-[50px]">
      <div className="flex flex-col gap-9 sm:gap-12 md:gap-[60px] 2xl:gap-[80px] w-full max-w-[1130px] mx-auto">
        <h2
          className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"
          
        >
          Revealing sea turtle secrets
        </h2>

        <div className="flex flex-col gap-9 sm:gap-12 md:gap-[60px] 2xl:gap-[80px]">
          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <h3 className={sectionTitleClass} >
              Knowing the threats
            </h3>
            <div className="flex flex-col gap-2 sm:gap-3" >
              <p className={bodyClass}>
                Researchers are documenting how sea turtles encounter and eat plastic and other debris, and how they are
                affected by fishing gear, boat strikes and pollution washing into the sea from the land.
              </p>
              <p className={bodyClass}>
                Researchers&apos; work increasingly looks at how sea turtles face combined threats, including climate change
                and extreme weather.
              </p>
              <p className={bodyClass}>
                Understanding the threats helps design policies and actions for protecting turtles.
              </p>
            </div>
            <div className="w-full overflow-hidden" style={{ aspectRatio: "1920 / 1080" }}>
              <video
                className="w-full h-full object-cover"
                poster={KnowingThreatsPoster}
                controls
                playsInline
                preload="metadata"
              >
                <source src={KnowingThreatsVideo} type="video/mp4" />
              </video>
            </div>
          </div>

          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <h3 className={sectionTitleClass} >
              Rehabilitating turtles
            </h3>
            <div className="flex flex-col gap-2 sm:gap-3" >
              <p className={bodyClass}>
                When sea turtles are found injured or sick, they are diagnosed, treated and rehabilitated.
              </p>
              <p className={bodyClass}>
              Just like for people, there are many health tests that researchers use to decide the best treatment and know when a turtle is strong enough to return to the ocean. But we are still learning more about the best tests and indicators of health.
              </p>
              <p className={bodyClass}>
              Research from rescued sea turtles also helps us understand what dangers they face in the wild.              </p>
            </div>
            <div className="w-full overflow-hidden" style={{ aspectRatio: "1920 / 1080" }}>
              <video
                className="w-full h-full object-cover"
                poster={RehabTurtlesPoster}
                controls
                playsInline
                preload="metadata"
              >
                <source src={RehabTurtlesVideo} type="video/mp4" />
              </video>
            </div>
          </div>

          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <h3 className={sectionTitleClass} >
              Understanding sea turtle biology to protect them
            </h3>
            <div className="flex flex-col gap-2 sm:gap-3" >
              <p className={bodyClass}>
              Scientists study sea turtle bodies to understand what a healthy turtle looks like. They test blood and body condition to compare healthy and sick turtles. They want to know how turtles’ health reflects the health of the environment they live in.
              </p>
              <p className={bodyClass}>
              Increased understanding helps volunteers, Land and Sea Rangers and vets know how to care for sick turtles found around the Fraser Coast.
              </p>
            </div>
            <div className="w-full overflow-hidden" style={{ aspectRatio: "1920 / 1080" }}>
              <video
                className="w-full h-full object-cover"
                poster={UnderstandingBiologyPoster}
                controls
                playsInline
                preload="metadata"
              >
                <source src={UnderstandingBiologyVideo} type="video/mp4" />
              </video>
            </div>
          </div>

          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <h3 className={sectionTitleClass} >
            Discovering where turtles live, feed, breed and move            </h3>
            <div className="flex flex-col gap-2 sm:gap-3" >
              <p className={bodyClass}>
              Researchers track where turtles swim, feed and rest using satellite tags and genetics.
              </p>
              <p className={bodyClass}>
              From this work, we know many sea turtles that feed in Fraser Coast waters travel thousands of kilometres and are connected to nesting beaches in other parts of Australia and the Pacific.
              </p>
              <p className={bodyClass}>
              Tracking turtles helps us to understand and protect the habitat health of important feeding and nesting areas.
              </p>
            </div>
            <img
              src={DiscoveringTurtlesLiveImage}
              width={1130}
              height={622}
              alt="Researchers tracking sea turtle habitats and movement patterns"
              className="w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <h3 className={sectionTitleClass} >
              Untangling the cause and effect of disease
            </h3>
            <div className="flex flex-col gap-2 sm:gap-3" >
              <p className={bodyClass}>
              Sea turtle disease research is increasingly like detective work because of how sensitive turtles are to the complexity of their habitats.
              </p>
              <p className={bodyClass}>
              Scientists combine disease testing with turtle health check sand environmental information (like water quality, temperature and pollution) to work out causes of diseases, what makes them worse and the effects on turtles’ bodies.  
              </p>
              <p className={bodyClass}>
              This knowledge is used to find patterns and improve rescue, rehabilitation and treatment.
              </p>
            </div>
            <img
              src={UntanglingCauseImage}
              width={960}
              height={448}
              alt="Sea turtle disease monitoring and diagnostics"
              className="w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="flex flex-col gap-5 sm:gap-6 md:gap-8 2xl:gap-[40px]">
            <h3 className={sectionTitleClass} >
              Acting locally, thinking globally
            </h3>
            <div className="flex flex-col gap-2 sm:gap-3" >
              <p className={bodyClass}>
              Scientists are learning how sea turtles are often affected by many problems at once – such as pollution, climate change, habitat loss and disease – and that those problems can interact.
              </p>
              <p className={bodyClass}>
              Our research helps to identify the most important ways, places and times to protect and treat turtles – and people around the world are interested in what we are discovering.
              </p>
              <p className={bodyClass}>
              Better knowledge improves Land and Sea Ranger programs, strengthens community efforts, and helps protect sea turtles for future generations all around the world.
              </p>
            </div>
            <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
              <img
                src={ActingLocallyImage}
                width={2048}
                height={947}
                alt="Community and research activities supporting global sea turtle conservation"
                className="w-full h-auto object-cover"
                loading="lazy"
                decoding="async"
              />
              <p className="text-black text-[12px] font-light leading-[1.2em] italic">
                Photo by Matt Barros
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Butchulla Country Section - Land, sky and sea connection
export function ButchullaCountrySection() {
  return (
    <section
      id="butchulla-care-for-land"
      className="section-lazy xbg-[#003D92] py-12 sm:py-16 md:py-20 2xl:py-[120px] px-4 sm:px-6 md:px-8 2xl:px-[50px] bg-[#f1f1f1] bg-center bg-no-repeat"
      // style={{ backgroundImage: `url(${ButchullaCareBg})` }}
    >
      <div className="flex flex-col gap-9 sm:gap-12 md:gap-[60px] 2xl:gap-[80px] w-full max-w-[1130px] mx-auto">
        <h2
          className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2] w-full "

        >
          Butchulla care for land, sea and sky Country
        </h2>

        <div
          className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-black font-light leading-[1.2]"
          
        >
          <p>
            We are committed custodians of Butchulla Country (land, sea and sky)
            responsible for protecting the ecological balance.
          </p>
          <p>
            Butchulla People&apos;s connection to Country (land, sea and sky) is
            interconnected. Our Sea Country provides not only sustenance but is
            also intricately connected to our spiritual and cultural identity.
          </p>
          <p>
            Sky Country connects us to our old people and to our creator
            Beerall(Beiral) and his messenger Yindingie who created all living
            things on Butchulla Country. Yindingie&apos;s footprint at Urangan
            reminds us that he was here on Country.
          </p>
        </div>

        <h2
        id="butchulla-culture-community"
          className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"

        >
          Culture, community and conservation
        </h2>

        <div
          className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] text-black font-light leading-[1.2]"
          
        >
          <p>
            We are custodians of a living culture and by nurturing this
            connection we ensure that the threads of our heritage remain
            unbroken for generations to come.
          </p>
          <p>
            Butchulla People know that our actions today shape the experiences of
            future generations. We have the responsibility for continuing our
            connection to Country for those who will come after us.
          </p>
          <p>
            As custodians of Butchulla Country (land, sea and sky) we share a
            responsibility for its wellbeing. We have a duty to protect the
            ecosystems, respect the flora and fauna and maintain the waterways.
          </p>
        </div>

        <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
          <div className="w-full overflow-hidden" style={{ aspectRatio: "1920 / 1080" }}>
            <video
              className="w-full h-full object-cover"
              poster={ButchullaCarePoster}
              controls
              playsInline
              preload="metadata"
            >
              <source src={ButchullaCareVideo} type="video/mp4" />
            </video>
          </div>
          <p className="text-black text-[12px] font-light leading-[1.2em] italic">
            Travis Page (Uncle Travis, Butchulla Songman)
          </p>
        </div>
      </div>
    </section>
  );
}

// Culture Community Conservation Section
export function CultureCommunitySection() {
  return (
    <section id="butchulla-care-for-country" className="section-lazy bg-white py-12 sm:py-16 md:py-20 2xl:py-[120px] px-4 sm:px-6 md:px-8 2xl:px-[50px]">
      <div className="flex flex-col gap-9 sm:gap-12 md:gap-[60px] 2xl:gap-[80px] w-full max-w-[1130px] mx-auto">
        <h2
          className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"
          
        >
          Butchulla care for Country
        </h2>

        <div
          className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] font-light text-black leading-[1.2]"
          
        >
          <p>
            Butchulla People&apos;s connection to Country is where knowledge, stories and traditions are passed down
            through generations, which ensures sustainability and prosperity.
          </p>
          <p>
            Butchulla People are custodians of our Country, which extends south to Double Island Point, east to
            K&apos;gari, west to Bauple Mountain and north to the mouth of the Burrum River.
          </p>
          <p>
            We monitor the health of rivers, creeks and the Great Sandy Strait; maintain cultural burning practices;
            revegetate land; protect habitats; and preserve our sacred and significant cultural heritage.
          </p>
        </div>

        <div className="w-full overflow-hidden" style={{ aspectRatio: "1920 / 1070" }}>
          <video
            className="w-full h-full object-cover"
            poster={ButchullaCareForCountryPoster}
            controls
            playsInline
            preload="metadata"
          >
            <source src={ButchullaCareForCountryVideo} type="video/mp4" />
          </video>
        </div>
      </div>
    </section>
  );
}

// Dayman Point Section - Meeting place
export function DaymanPointSection() {
  return (
    <section id="dayman-point" className="section-lazy bg-[#F2F2F2] py-12 sm:py-16 md:py-20 2xl:py-[120px] px-4 sm:px-6 md:px-8 2xl:px-[50px]">
      <div className="flex flex-col gap-9 sm:gap-12 md:gap-[60px] 2xl:gap-[80px] w-full max-w-[1130px] mx-auto">
        <h2
          className="text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px] font-medium text-black leading-[1.2]"
          
        >
          Dayman Point and Dayman Park
        </h2>

        <div
          className="flex flex-col gap-2 sm:gap-3 text-[14px] sm:text-[16px] md:text-[17px] 2xl:text-[18px] font-light text-black leading-[1.2]"
          
        >
          <p>
            Dayman Point/Dayman Park is significant to Butchulla People because of its connection to our Creation
            story. Yindingie, the messenger who created all living things on Butchulla Country, returned to check on
            the animals and people he created. As evidence of his visit, Yindingie left his footprint at Urangan, then
            took another step to Bauple Mountain leaving another footprint before returning to Sky Country.
          </p>
          <p>
            This area is also remembered by Butchulla People for holding one of the last public men&apos;s ceremonial
            events on Butchulla Country in 1925.
          </p>
        </div>

        <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
          <img
            src={DaymanPointImage}
            width={2260}
            height={888}
            alt="Dayman Point and Dayman Park on Butchulla Country"
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
          <p className="text-black text-[12px] font-light leading-[1.2em] italic">
            Read about our Creation stories in: "The Legends of Moonie Jarl" written and illustrated by Wilf Reeves and
            Olga Miller. Right: Corroboree in Dayman Park
          </p>
        </div>
      </div>
    </section>
  );
}

// Footer Component
export function Footer() {
  return (
    <footer className="bg-black min-h-[96px] sm:min-h-[112px] md:min-h-[128px] 2xl:min-h-[144px] px-4 sm:px-6 md:px-8 2xl:px-[40px] py-6 sm:py-8 md:py-10 2xl:py-[65px]">
      <div className="w-full flex items-center justify-between gap-4 sm:gap-5 md:gap-6 2xl:gap-8">
        <img
          src={LogoWhite}
          width={128}
          height={108}
          alt="Milbi Centre - Sea Turtle Research and Rehabilitation"
          className="h-[32px] sm:h-[38px] md:h-[44px] 2xl:h-[50px] w-auto"
        />
        <div className="flex items-center gap-2 sm:gap-3 md:gap-[18px] 2xl:gap-[20px]">
          <a
            href="#"
            className="text-[12px] sm:text-[13px] md:text-[14px] 2xl:text-[16px] text-white underline leading-[1.2]"
            
          >
            Privacy policy
          </a>
          <a
            href="#"
            className="text-[12px] sm:text-[13px] md:text-[14px] 2xl:text-[16px] text-white underline leading-[1.2]"
            
          >
            Terms & conditions
          </a>
        </div>
      </div>
    </footer>
  );
}
